import React from "react"
import { FormattedMessage } from "react-intl"

export const CareerPlanColors = {
  PINK: {
    color: "var(--w-color-pink-500)",
    label: <FormattedMessage id="career-plan-colors-pink" defaultMessage="Pink" />
  },
  PURPLE: {
    color: "var(--w-color-purple-500)",
    label: <FormattedMessage id="career-plan-colors-purple" defaultMessage="Purple" />
  },
  BLUE: {
    color: "var(--w-color-blue-500)",
    label: <FormattedMessage id="career-plan-colors-blue" defaultMessage="Blue" />
  },
  TEAL: {
    color: "var(--w-color-teal-400)",
    label: <FormattedMessage id="career-plan-colors-teal" defaultMessage="Teal" />
  },
  ORANGE: {
    color: "var(--w-color-orange-300)",
    label: <FormattedMessage id="career-plan-colors-orange" defaultMessage="Orange" />
  },
  YELLOW: {
    color: "var(--w-color-yellow-500)",
    label: <FormattedMessage id="career-plan-colors-yellow" defaultMessage="Yellow" />
  },
}

// Milestone category slugs, ordered approximately from lowest to highest education level
const EDUCATION_LEVEL_SLUGS = [
  "high-school-diploma",
  "classes",
  "training",
  "portfolio",
  "certificate",
  "internship",
  "milestone-apprenticeship",
  "work-experience",
  "exam",
  "professional-license",
  "associate-degree",
  "bachelors-degree",
  "masters-degree",
  "doctorate2",
  "residency",
  "post-doctorate",
  "fellowship",
]
const milestoneToEducationLevel = {}
EDUCATION_LEVEL_SLUGS.forEach((slug, idx) => 
  milestoneToEducationLevel[slug] = idx + 1
)

/**
 * Sorts Cosmic guided career plans from lowest to highest education level.
 * Orders plans using these factors in this order of priority:
 * 1) milestone with the highest education level
 * 2) education level of first milestone
 * 3) alphabetical ordering of plan titles
 * @param {Object[]} cosmicPlans 
 * @returns {Object[]}
 */
export const sortCosmicPlansByEducationLevel = (cosmicPlans = []) => {
  const idToEduLevel = {}
  const idToStartEduLevel = {}
  cosmicPlans.forEach(plan => {
    const milestones = plan.metadata.milestones || []
    const milestoneEduLevels = milestones.map(
      m => milestoneToEducationLevel[m.category?.slug] || 0
    )
    idToEduLevel[plan.id] = Math.max(0, ...milestoneEduLevels)
    idToStartEduLevel[plan.id] = milestoneEduLevels[0] || 0
  })
  return [...cosmicPlans].sort((a, b) => {
    const edLevelDiff = idToEduLevel[a.id] - idToEduLevel[b.id]
    const startEdLevelDiff = idToStartEduLevel[a.id] - idToStartEduLevel[b.id]
    return edLevelDiff || startEdLevelDiff || a.title.localeCompare(b.title)
  })
}

/**
 * Cosmic milestones reference their category as a Milestone object (`{ slug, ... }`).
 * Flattens that down to the slug which is what is stored in Firestore and what is used
 * by display components.
 */
export const normalizeCosmicMilestoneCategories = (cosmicMilestones = []) =>
  cosmicMilestones.map(milestone => ({
    ...milestone,
    category: milestone.category?.slug,
  }))

export const careerPlanFromCosmicGuidedCareerPlan = (cosmicPlan) => {
  return {
    title: cosmicPlan.title,
    color: "PINK",
    milestones: (cosmicPlan.metadata.milestones || []).map(milestone => ({
      category: milestone.category?.slug,
      description: milestone.description,
      organization: milestone.organization,
      completed: false,
      startDate: "0000-00",
      endDate: "0000-00",
    }))
  }
}

const samplePlanTemplate = {
  id: "sample",
  title: "Sample Plan",
  titleEs: "Plan de muestra",
  color: "PINK",
  milestones: [
    {
      id: "sample-associate",
      category: "associate-degree",
      description: "Registered Nursing, Nursing Administration, Nursing Research and Clinical Nursing, Other",
      descriptionEs: "Enfermería Registrada, Administración de Enfermería, Investigación de Enfermería y Enfermería Clínica, Otros",
      organization: "Anna Maria College",
      organizationEs: "Colegio Anna Maria",
      completed: false,
      startDate: "2012-09",
      endDate: "2014-08",
    },
    {
      id: "sample-work1",
      category: "work-experience",
      description: "Case Management Nurse",
      descriptionEs: "Enfermera de Administración de Casos",
      organization: "Fallon Health",
      organizationEs: "Salud de Fallon",
      completed: false,
      startDate: "2014-09",
      endDate: "2021-09",
    },
    {
      id: "sample-bachelors",
      category: "bachelors-degree",
      description: "Nursing Practice",
      descriptionEs: "Práctica de enfermería",
      organization: "Southern New Hampshire University",
      organizationEs: "Universidad del Sur de New Hampshire",
      completed: false,
      startDate: "2015-01",
      endDate: "2016-12",
    },
    {
      id: "sample-masters",
      category: "masters-degree",
      description: "Family Nurse Practitioner",
      descriptionEs: "Enfermera Practicante de Familia",
      organization: "Massachusetts College of Pharmacy and Health Sciences",
      organizationEs: "Facultad de Farmacia y Ciencias de la Salud de Massachusetts",
      completed: false,
      startDate: "2019-09",
      endDate: "2021-09",
    },
    {
      id: "sample-work2",
      category: "work-experience",
      description: "Nurse Practitioner",
      descriptionEs: "Enfermera practicante",
      organization: "Harrington Physician Services",
      organizationEs: "Servicios médicos de Harrington",
      completed: false,
      startDate: "2022-07",
      endDate: "2023-08",
    },
  ],
}

// The dates of an unedited sample plan should be updated to use next 
// September/January as the start date
const currentDate = new Date()
const currentYear = currentDate.getFullYear()
const currentMonth = currentDate.getMonth()
const planStartMonth = currentMonth >= 8 ? 1 : 8
const planStartYear = currentMonth >= 8 ? currentYear + 1 : currentYear
const monthsBetween = (startDate, endDate) => {
  const [startYear, startMonth] = startDate.split("-").map(Number)
  const [endYear, endMonth] = endDate.split("-").map(Number)
  return ((endYear - startYear) * 12) + (endMonth - startMonth)
}
const addMonthsToDate = (dateString, months) => {
  const [year, month] = dateString.split("-").map(Number)
  const totalMonths = (12 * year) + (month - 1) + months
  return `${Math.floor(totalMonths / 12)}-${String((totalMonths % 12) + 1).padStart(2, "0")}`
}
const monthsToShift = monthsBetween(samplePlanTemplate.milestones[0].startDate, `${planStartYear}-${String(planStartMonth).padStart(2, "0")}`)

export const samplePlan = structuredClone(samplePlanTemplate)
samplePlan.milestones.forEach(m => {
  m.startDate = addMonthsToDate(m.startDate, monthsToShift)
  m.endDate = addMonthsToDate(m.endDate, monthsToShift)
})

export const samplePlanEs = structuredClone(samplePlan)
samplePlanEs.title = samplePlanEs.titleEs
samplePlanEs.milestones.forEach(m => {
  m.description = m.descriptionEs
  m.organization = m.organizationEs
})
